import { useState } from 'react';
import { Card, Grid, CircularProgress, TextField } from '@mui/material';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import MDButton from 'src/components/md/MDButton';
import ErrorMessage from 'src/components/errorMessage/ErrorMessage';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import useCreateOutboundSellSuggestionNotification from 'src/api/qa/createOutboundSellSuggestionNotification';
import useReprocessInboundEmail from 'src/api/qa/useReprocessInboundEmail';
import useRemoveShipmentSignature from 'src/api/qa/useRemoveShipmentSignature';
import useGenerateSampleInvoice from 'src/api/qa/useGenerateSampleInvoice';
import useQueueUpReceivedPOEvent from 'src/api/qa/useQueueUpReceivedPOEvent';
import useQueueUpApprovedPOEvent from 'src/api/qa/useQueueUpApprovedPOEvent';

function QAPage() {
  const {
    mutate: queueUpApprovedPOEvent,
    isLoading: isLoadingQueueUpApprovedPOEvent,
    error: errorQueueUpApprovedPOEvent,
  } = useQueueUpApprovedPOEvent();

  const {
    mutate: queueUpReceivedPOEvent,
    isLoading: isLoadingQueueUpReceivedPOEvent,
    error: errorQueueUpReceivedPOEvent,
  } = useQueueUpReceivedPOEvent();

  const {
    mutate: createOutboundSellSuggestionNotification,
    isLoading: isLoadingCreateOutboundSellSuggestionNotification,
  } = useCreateOutboundSellSuggestionNotification();

  const {
    mutate: reprocessInboundEmail,
    isLoading: isLoadingReprocessInboundEmail,
  } = useReprocessInboundEmail();

  const [shipmentId, setShipmentId] = useState('');
  const {
    mutate: removeShipmentSignature,
    isLoading: isLoadingRemoveShipmentSignature,
  } = useRemoveShipmentSignature();

  const [orderId, setOrderId] = useState('');
  const { mutate: generateSampleInvoice, isLoading: isLoadingGenerateInvoice } =
    useGenerateSampleInvoice();

  return (
    <DashboardLayout>
      <MDBox mt={4} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12}>
            <MDBox mb={3}>
              <MDTypography variant="h4" fontWeight="medium">
                QA Testing Flows & Tools
              </MDTypography>
              <MDTypography variant="body2" color="text">
                Select a flow to test specific functionality
              </MDTypography>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>

      <Card sx={{ mb: 2 }}>
        <MDBox p={2}>
          <MDTypography variant="h6">
            Queue Customer PO Approved Event
          </MDTypography>
          <MDTypography variant="body2" color="text" my={2}>
            {`Clicking this button will queue up a customer PO approved event. It'll select random existing PO.`}
          </MDTypography>
          <ErrorMessage error={errorQueueUpApprovedPOEvent} />
          <MDBox textAlign="right">
            <MDButton
              variant="contained"
              color="primary"
              onClick={() => queueUpApprovedPOEvent()}
              disabled={isLoadingQueueUpApprovedPOEvent}
            >
              {isLoadingQueueUpApprovedPOEvent ? (
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              ) : null}
              Queue Customer PO Approved Event
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>

      <Card sx={{ mb: 2 }}>
        <MDBox p={2}>
          <MDTypography variant="h6">Queue PO Received Event</MDTypography>
          <MDTypography variant="body2" color="text" my={2}>
            {`Clicking this button will queue up a received PO event. It'll select random existing PO.`}
          </MDTypography>
          <ErrorMessage error={errorQueueUpReceivedPOEvent} />
          <MDBox textAlign="right">
            <MDButton
              variant="contained"
              color="primary"
              onClick={() => queueUpReceivedPOEvent()}
              disabled={isLoadingQueueUpReceivedPOEvent}
            >
              {isLoadingQueueUpReceivedPOEvent ? (
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              ) : null}
              Queue PO Received Event
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>

      <Card sx={{ mb: 2 }}>
        <MDBox p={2}>
          <MDTypography variant="h6">
            Create Outbound Sell Suggestion Notification
          </MDTypography>
          <MDTypography variant="body2" color="text" my={2}>
            {`Clicking this button will create an outbound sell suggestion
      notification for the customer. It'll select random customer, product
      and amount.`}
          </MDTypography>
          <MDBox textAlign="right">
            <MDButton
              variant="contained"
              color="primary"
              onClick={() => createOutboundSellSuggestionNotification()}
              disabled={isLoadingCreateOutboundSellSuggestionNotification}
            >
              {isLoadingCreateOutboundSellSuggestionNotification ? (
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              ) : null}
              Create Outbound Sell Suggestion Notification
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>

      <Card sx={{ mb: 2 }}>
        <MDBox p={2}>
          <MDTypography variant="h6">Reprocess Inbound Email</MDTypography>
          <MDTypography variant="body2" color="text" my={2}>
            {`Clicking this button will reprocess an inbound email. It'll select the last inbound email if no ID is provided.`}
          </MDTypography>
          <MDBox textAlign="right">
            <MDButton
              variant="contained"
              color="primary"
              onClick={() => reprocessInboundEmail()}
              disabled={isLoadingReprocessInboundEmail}
            >
              {isLoadingReprocessInboundEmail ? (
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              ) : null}
              Reprocess Inbound Email
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>

      <Card sx={{ mb: 2 }}>
        <MDBox p={2}>
          <MDTypography variant="h6">Remove Shipment Signature</MDTypography>
          <MDTypography variant="body2" color="text" my={2}>
            {`Remove the signature from a shipment's bill of lading and proof of
            delivery documents. This is useful for testing signature flows.`}
          </MDTypography>

          <MDBox display="flex" alignItems="center" gap={2}>
            <TextField
              label="Shipment ID"
              value={shipmentId}
              onChange={(e) => setShipmentId(e.target.value)}
              size="small"
              sx={{ flexGrow: 1 }}
              disabled={isLoadingRemoveShipmentSignature}
            />
            <MDButton
              variant="contained"
              color="primary"
              onClick={() => {
                if (shipmentId) {
                  removeShipmentSignature(
                    { shipmentId },
                    {
                      onSuccess: () => {
                        window.alert('Signature removed successfully');
                      },
                      onError: () => {
                        window.alert('Error removing signature');
                      },
                    },
                  );
                }
              }}
              disabled={!shipmentId || isLoadingRemoveShipmentSignature}
            >
              {isLoadingRemoveShipmentSignature ? (
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              ) : null}
              Remove Signature
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>

      <Card sx={{ mb: 2 }}>
        <MDBox p={2}>
          <MDTypography variant="h6">Generate Sample Invoice PDF</MDTypography>
          <MDTypography variant="body2" color="text" my={2}>
            Generate a sample invoice PDF from a purchase order. This is useful
            for testing invoice PDF generation.
          </MDTypography>

          <MDBox display="flex" alignItems="center" gap={2}>
            <TextField
              label="Order ID"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              size="small"
              sx={{ flexGrow: 1 }}
              disabled={isLoadingGenerateInvoice}
            />
            <MDButton
              variant="contained"
              color="primary"
              onClick={() => {
                if (orderId) {
                  generateSampleInvoice(
                    { orderId },
                    {
                      onSuccess: (response) => {
                        window.open(response.data.url, '_blank');
                      },
                      onError: () => {
                        window.alert('Error generating invoice PDF');
                      },
                    },
                  );
                }
              }}
              disabled={!orderId || isLoadingGenerateInvoice}
            >
              {isLoadingGenerateInvoice ? (
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              ) : null}
              Generate PDF
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
    </DashboardLayout>
  );
}

export default QAPage;
